'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuth, useUser } from '@clerk/nextjs';
import { useEffect, useRef, useState } from 'react';
import { identifyUser } from '@/features/tracking';
import { getUserRowContext, trackRegistrationEvents } from '@/features/tracking/actions';
type AuthSyncStorage = Pick<Storage, 'getItem' | 'setItem'>;
type AuthSyncTrackingDeps = {
  identifyUser: typeof identifyUser;
  trackRegistrationEvents: typeof trackRegistrationEvents;
  storage: AuthSyncStorage;
};
export async function syncAuthTrackingIdentity(rowId: string, email: string, signupBrandId: string | null, deps: AuthSyncTrackingDeps) {
  const registrationKey = `registration_tracked_${rowId}`;
  let shouldTrackRegistration = true;
  try {
    shouldTrackRegistration = !deps.storage.getItem(registrationKey);
  } catch {
    // Optional browser persistence must not prevent identity synchronization.
  }
  const [identifyResult, registrationResult] = await Promise.allSettled([deps.identifyUser(rowId, {
    user_email: email,
    ...(signupBrandId ? {
      signup_brand_id: signupBrandId
    } : {})
  }), shouldTrackRegistration ? deps.trackRegistrationEvents() : Promise.resolve()]);
  if (shouldTrackRegistration && registrationResult.status === 'fulfilled') {
    try {
      deps.storage.setItem(registrationKey, 'true');
    } catch {
      // Successful synchronization must not fail because the browser cannot persist it.
    }
  }
  if (identifyResult.status === 'rejected') {
    console.error('Failed to identify auth tracking user:', identifyResult.reason);
  }
  if (shouldTrackRegistration && registrationResult.status === 'rejected') {
    console.error('Failed to track registration events:', registrationResult.reason);
  }
}
export function AuthSync() {
  const $ = _c(19);
  const hasIdentified = useRef(false);
  const [rowId, setRowId] = useState(null);
  const [signupBrandId, setSignupBrandId] = useState(null);
  const {
    isLoaded,
    isSignedIn,
    userId
  } = useAuth();
  const {
    user
  } = useUser();
  let t0;
  if ($[0] !== isLoaded || $[1] !== isSignedIn) {
    t0 = () => {
      if (!isLoaded) {
        return;
      }
      hasIdentified.current = false;
      setRowId(null);
      setSignupBrandId(null);
      if (!isSignedIn) {
        return;
      }
      getUserRowContext().then(ctx => {
        if (ctx.id) {
          setRowId(ctx.id);
        }
        if (ctx.signupBrandId) {
          setSignupBrandId(ctx.signupBrandId);
        }
      }).catch(_temp);
    };
    $[0] = isLoaded;
    $[1] = isSignedIn;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== isLoaded || $[4] !== isSignedIn || $[5] !== userId) {
    t1 = [isLoaded, isSignedIn, userId];
    $[3] = isLoaded;
    $[4] = isSignedIn;
    $[5] = userId;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  useEffect(t0, t1);
  let t2;
  if ($[7] !== isLoaded || $[8] !== isSignedIn || $[9] !== rowId || $[10] !== signupBrandId || $[11] !== user?.primaryEmailAddress?.emailAddress) {
    t2 = () => {
      if (!isLoaded || !isSignedIn || !rowId || hasIdentified.current) {
        return;
      }
      const email = user?.primaryEmailAddress?.emailAddress || "";
      if (!email) {
        return;
      }
      syncAuthTrackingIdentity(rowId, email, signupBrandId, {
        identifyUser,
        trackRegistrationEvents,
        storage: {
          getItem: _temp2,
          setItem: _temp3
        }
      }).catch(_temp4);
      hasIdentified.current = true;
    };
    $[7] = isLoaded;
    $[8] = isSignedIn;
    $[9] = rowId;
    $[10] = signupBrandId;
    $[11] = user?.primaryEmailAddress?.emailAddress;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== isLoaded || $[14] !== isSignedIn || $[15] !== rowId || $[16] !== signupBrandId || $[17] !== user) {
    t3 = [isLoaded, isSignedIn, rowId, signupBrandId, user];
    $[13] = isLoaded;
    $[14] = isSignedIn;
    $[15] = rowId;
    $[16] = signupBrandId;
    $[17] = user;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  useEffect(t2, t3);
  return null;
}
function _temp4() {}
function _temp3(key_0, value) {
  return localStorage.setItem(key_0, value);
}
function _temp2(key) {
  return localStorage.getItem(key);
}
function _temp(error) {
  console.error("Failed to get user row context:", error);
}